import { Box, Divider, Grid, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { useSetSelectedItem } from '../services/cartManager';
import { formatCurrency } from '../formattingUtils';
import { CheckIcon, EditIcon } from '../icons';
import { useGetAdvancedCartDetails } from '../services/cartManager';
import { useState } from 'react';
import { useSetCurrentModal } from '../services/modalManager';
import { CartItem } from '../services/flowManager';
import { TrackerIconButton } from './TrackerButtonWrapper';
import { ButtonName } from '../constants';

const ItemsListContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '100%',
  padding: theme.spacing(0, 4, 0, 4),
  overflow: 'hidden',
  position: 'relative',
}));

const StyledTrackerIconButton = styled(TrackerIconButton)({
  padding: 0,
});

const AdvancedCartDetails = styled(Box)({
  overflowY: 'scroll',
  width: '100%',
  height: '100%',
  scrollbarWidth: 'none',
  '::-webkit-scrollbar': {
    display: 'none',
  },
});

const StyledGradient = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  width: '100%',
  height: theme.spacing(4),
  background: `linear-gradient(transparent, ${theme.palette.background.paper})`,
}));

export const ItemsList = () => {
  const advancedCartDetails = useGetAdvancedCartDetails();

  return (
    <ItemsListContainer>
      <AdvancedCartDetails>
        {advancedCartDetails.items.map((item, index) => (
          <ItemRow
            rowNumber={index}
            item={item}
            key={`${item.name}-${index}`}
          />
        ))}
        <StyledGradient />
      </AdvancedCartDetails>
    </ItemsListContainer>
  );
};

interface ItemRowProps {
  item: CartItem;
  key: string;
  rowNumber: number;
}

const ItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%',
  padding: theme.spacing(1, 0),
}));

const LeasableText = styled(Typography)(({ theme }) => ({
  color: theme.palette.success.dark,
  padding: 0,
  marginLeft: theme.spacing(0.5),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.grey[200],
}));

const StyledGridItem = styled(Grid)(({ theme }) => ({
  textAlign: 'left',
  display: 'flex',
  gap: theme.spacing(1),
}));

const StyledCheckIcon = styled(CheckIcon)(({ theme }) => ({
  color: theme.palette.success.dark,
  verticalAlign: 'middle',
}));

const ItemName = styled(Typography)<{ expanded: boolean }>(
  ({ theme, expanded }) => ({
    fontWeight: 'bold',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: expanded ? 'normal' : 'nowrap',
    width: theme.spacing(38),
    height: expanded ? 'fit-content' : 'auto',
    cursor: 'pointer',
  }),
);

const StyledEditIcon = styled(EditIcon)({
  width: 18,
  height: 18,
});

const ItemRow = ({ item, rowNumber }: ItemRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const updateCurrentModal = useSetCurrentModal();
  const setEditedItem = useSetSelectedItem();
  const openEditModal = () => {
    setEditedItem(item);
    updateCurrentModal('cartItem');
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <>
      <ItemContainer data-testid={`row-index-${rowNumber}`}>
        <Grid container spacing={1}>
          <StyledGridItem item xs={4}>
            <ItemName
              data-testid="list-item-name"
              expanded={expanded}
              onClick={toggleExpanded}
              variant="body2"
            >
              {item.name}
            </ItemName>
          </StyledGridItem>
          <StyledGridItem item xs={2}>
            <Typography
              p={0}
              data-testid="list-item-sku"
              variant="body4"
            >{`SKU: ${item.sku}`}</Typography>
          </StyledGridItem>
          <StyledGridItem item xs={2}>
            {item.leasable && (
              <Box data-testid="list-item-leasable">
                <StyledCheckIcon />
                <LeasableText variant="body4">LEASABLE</LeasableText>
              </Box>
            )}
          </StyledGridItem>
          <StyledGridItem item xs={2}>
            <Typography
              data-testid="list-item-quantity"
              variant="body2"
            >{`QTY: ${item.quantity}`}</Typography>
          </StyledGridItem>
          <StyledGridItem item xs={1}>
            <Typography>{formatCurrency(+item.price)}</Typography>
          </StyledGridItem>
          {item.warranty && (
            <>
              <StyledGridItem item xs={4}>
                <Typography data-testid="list-warranty-name" variant="body1">
                  {item.warranty.name}
                </Typography>
              </StyledGridItem>
              <StyledGridItem item xs={2}>
                <Typography
                  p={0}
                  variant="body4"
                  data-testid="list-warranty-sku"
                >{`SKU: ${item.warranty.sku}`}</Typography>
              </StyledGridItem>
              <StyledGridItem item xs={4} />
              <StyledGridItem item xs={1}>
                <Typography data-testid="list-warranty-price">
                  {formatCurrency(+item.warranty.price)}
                </Typography>
              </StyledGridItem>
            </>
          )}
        </Grid>
        <StyledTrackerIconButton
          buttonName={ButtonName.EditItem}
          data-testid="edit-icon"
          onClick={openEditModal}
        >
          <StyledEditIcon />
        </StyledTrackerIconButton>
      </ItemContainer>
      <StyledDivider />
    </>
  );
};
